import React, {
  Fragment, useState, useCallback, useEffect
} from 'react';
import PropTypes from 'prop-types';
import IntersectionObserver from '@researchgate/react-intersection-observer';

import 'src/components/AutopromoPicture.scss';


const AutopromoPictureSource = ({ images, imgUrlElement }) => {
  const [sourceImagesUrl, setSourceImagesUrl] = useState('');

  const getImages = useCallback(({ data }) => data
    .filter(({ url }) => images[String(url)])
    .map(({ url, density }) => `${images[String(url)]} ${density}x`)
    .join(', '), [images]);

  const media = `(min-width: ${imgUrlElement.width}px)`;

  useEffect(() => {
    setSourceImagesUrl(getImages({ data: imgUrlElement.imgs }));
  }, [
    getImages,
    images,
    imgUrlElement
  ]);

  return (
    <source
      media={ media }
      srcSet={ sourceImagesUrl }
      styleName="autopromo-picture__source"
    />
  );
};

const AutopromoImage = ({ imageUrl, altText }) => (<img alt={ altText } src={ imageUrl } styleName="autopromo-img" />);

const AutopromoPicture = ({ images, imgUrl, altText }) => {
  const [shouldRender, setShouldRender] = useState(false);

  const observerCallback = useCallback(({ isIntersecting }, unobserve) => requestIdleCallback(() => {
    if (isIntersecting) {
      setShouldRender(true);
      unobserve();
    }
  }), []);

  const renderPicture = () => (
    <Fragment>
      {
        imgUrl
          .sort((imgUrlElementA, imgUrlElementB) => imgUrlElementB.width - imgUrlElementA.width)
          .map(imgUrlElement => (
            <Fragment key={ imgUrlElement.width }>
              <AutopromoPictureSource images={ images } imgUrlElement={ imgUrlElement } />
            </Fragment>
          ))
      }
      <AutopromoImage
        altText={ altText }
        imageUrl={ images[imgUrl[imgUrl.length - 1].imgs[0].url] }
      />
    </Fragment>
  );

  return (
    <IntersectionObserver onChange={ observerCallback } rootMargin="100px">
      <picture styleName="autopromo-picture">
        { shouldRender ? renderPicture() : '' }
      </picture>
    </IntersectionObserver>
  );
};

AutopromoPictureSource.propTypes = {
  images: PropTypes.shape({
    desktop1x: PropTypes.string,
    desktop2x: PropTypes.string,
    mobile1x: PropTypes.string,
    mobile2x: PropTypes.string,
    mobile3x: PropTypes.string
  }),
  imgUrlElement: PropTypes.shape({
    imgs: PropTypes.arrayOf(Object),
    width: PropTypes.number
  }).isRequired
};

AutopromoPictureSource.defaultProps = {
  images: {}
};

AutopromoImage.propTypes = {
  altText: PropTypes.string.isRequired,
  imageUrl: PropTypes.string.isRequired
};

AutopromoPicture.propTypes = {
  altText: PropTypes.string.isRequired,
  images: PropTypes.shape({
    desktop1x: PropTypes.string,
    desktop2x: PropTypes.string,
    mobile1x: PropTypes.string,
    mobile2x: PropTypes.string,
    mobile3x: PropTypes.string
  }),
  imgUrl: PropTypes.arrayOf(PropTypes.shape({
    imgs: PropTypes.arrayOf(PropTypes.shape({
      density: PropTypes.number,
      url: PropTypes.string
    })),
    width: PropTypes.number
  })).isRequired
};

AutopromoPicture.defaultProps = {
  images: {}
};

export default AutopromoPicture;
